import React, { useState, useEffect } from 'react';
import {
  Team, 
  User, 
  ClubMember,
  ClubActivity,
  TeamReport,
  getAllTeams,
  getTeamById,
  getTeamsByTournamentId,
  getTeamsByAdminId,
  getTeamsByMemberId,
  createTeam,
  updateTeam,
  deleteTeam,
  addMemberToTeam,
  removeMemberFromTeam,
  getTeamMembers,
  getTeamActivities,
  getTeamActivitiesByDateRange,
  getTeamActivityReport,
  CreateTeamRequest,
  UpdateTeamRequest,
  mapAuthUserToTeamsUser,
  addTeamMemberByEmail,
  addTeamMemberByUserId,
  getTeamsByUserId
} from './services/teamsService';
import { getUserId, isAuthenticated, checkAuthStatus, isAdmin, getAuthHeader, getClubMemberId, getUserRoles, getUserInfo } from '../../services/authService';
import { getAllTournaments, Tournament } from '../../services/tournamentService';
import { getAllUsers } from '../../services/statisticsService';
import { getUsersStravaConnection, UserStravaConnection, StravaConnectionStatus } from '../../services/systemService';
import COLORS from '../../utils/colors';
import { useTheme } from '../../context/ThemeContext';
import { 
  FaUsers, 
  FaUserPlus, 
  FaSearch, 
  FaEye, 
  FaEdit, 
  FaTrash, 
  FaTimes, 
  FaSpinner, 
  FaExclamationTriangle,
  FaCalendarAlt,
  FaUserShield,
  FaInfoCircle,
  FaUserFriends,
  FaRunning,
  FaPlus,
  FaArrowRight,
  FaFolderOpen,
  FaFilter,
  FaTrophy,
  FaTimesCircle,
  FaStrava
} from 'react-icons/fa';
import './teams.css';
import ChatBox from '../../components/ChatBox';

// Thêm interface cho người dùng từ API
interface UserOption {
  userId: number;
  fullName: string;
  avatarUrl: string | null;
}

const Teams: React.FC = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [myTeams, setMyTeams] = useState<Team[]>([]);
  const [publicTeams, setPublicTeams] = useState<Team[]>([]);
  const [activeTab, setActiveTab] = useState<'myTeams' | 'publicTeams'>('publicTeams');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  
  // State cho modal tạo nhóm mới
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createTeamData, setCreateTeamData] = useState<CreateTeamRequest>({
    name: '',
    description: '',
    tournamentId: undefined
  });
  const [createLoading, setCreateLoading] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  
  // State cho danh sách giải đấu
  const [tournaments, setTournaments] = useState<Tournament[]>([]);
  const [tournamentsLoading, setTournamentsLoading] = useState(false);
  
  // State cho modal xem chi tiết nhóm và quản lý thành viên
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedTeam, setSelectedTeam] = useState<Team | null>(null);
  const [teamMembers, setTeamMembers] = useState<ClubMember[]>([]);
  const [newMemberEmail, setNewMemberEmail] = useState('');
  const [newMemberUserId, setNewMemberUserId] = useState<number | string>('');
  const [memberActionLoading, setMemberActionLoading] = useState(false);
  const [memberActionError, setMemberActionError] = useState<string | null>(null);
  const [searchMemberQuery, setSearchMemberQuery] = useState('');
  
  // State cho modal cập nhật nhóm
  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const [updateTeamData, setUpdateTeamData] = useState<UpdateTeamRequest>({
    name: '',
    description: '',
    tournamentId: undefined
  });
  const [updateLoading, setUpdateLoading] = useState(false);
  const [updateError, setUpdateError] = useState<string | null>(null);
  
  // Kiểm tra quyền admin
  const [isUserAdmin, setIsUserAdmin] = useState(false);
  
  // State cho trạng thái tham gia nhóm
  const [joinTeamLoading, setJoinTeamLoading] = useState(false);
  
  // State cho trạng thái rời nhóm
  const [leaveTeamLoading, setLeaveTeamLoading] = useState(false);
  
  // Thêm state cho danh sách nhóm và giải đấu mà người dùng đã tham gia
  const [userTeams, setUserTeams] = useState<{teamId: number, tournamentId?: number}[]>([]);
  const [userTeamId, setUserTeamId] = useState<number | null>(null);
  
  // Thêm state cho dialog message
  const [showMessageDialog, setShowMessageDialog] = useState(false);
  const [messageDialogContent, setMessageDialogContent] = useState({ title: '', message: '', type: 'success' });
  
  // State cho danh sách người dùng (để thêm vào nhóm)
  const [users, setUsers] = useState<UserOption[]>([]);
  const [filteredUsers, setFilteredUsers] = useState<UserOption[]>([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState<number | null>(null);
  const [userSearchQuery, setUserSearchQuery] = useState('');
  const [showUserDropdown, setShowUserDropdown] = useState(false);
  
  // State cho thông tin kết nối Strava
  const [stravaConnections, setStravaConnections] = useState<UserStravaConnection[]>([]);
  const [loadingStravaConnections, setLoadingStravaConnections] = useState(false);
  
  // Kiểm tra quyền admin
  useEffect(() => {
    // Import và gọi hàm debug
    import('../../services/authService').then(auth => {
      auth.debugAuth();
      
      // Kiểm tra và set quyền admin
      checkAuthStatus();
      console.log('Admin check - isAdmin():', isAdmin());
      
      // HACK: Uncomment dòng dưới đây để buộc hiển thị nút admin cho mục đích debug
      // setIsUserAdmin(true);
      
      setIsUserAdmin(isAdmin());
    });
  }, []);
  
  // Lấy thông tin người dùng từ token thay vì gọi API
  useEffect(() => {
    const fetchUserInfo = async () => {
      try {
        // Kiểm tra nếu đã đăng nhập
        if (!isAuthenticated()) {
          setError('Vui lòng đăng nhập để xem thông tin nhóm');
          setLoading(false);
          return;
        }
        
        // Lấy thông tin người dùng từ token thay vì gọi API
        const userInfo = mapAuthUserToTeamsUser();
        if (userInfo) {
          setCurrentUser(userInfo);
        } else {
          // Thông báo lỗi hoặc chuyển hướng đến trang đăng nhập
          console.error('Không thể lấy thông tin người dùng');
          setError('Không thể lấy thông tin người dùng. Vui lòng đăng nhập lại.');
        }
      } catch (error) {
        console.error('Lỗi khi lấy thông tin người dùng:', error);
      }
    };
    
    fetchUserInfo();
  }, []);
  
  // Hàm lấy dữ liệu nhóm công khai
  const fetchPublicTeams = async () => {
    try {
      setLoading(true);
      console.log('Bắt đầu lấy dữ liệu nhóm công khai');
      
      // Lấy tất cả các nhóm
      const allTeamsData = await getAllTeams();
      console.log('Đã nhận dữ liệu nhóm công khai:', allTeamsData);
      
      // Kiểm tra nếu là mảng
      if (Array.isArray(allTeamsData)) {
        setPublicTeams(allTeamsData);
        setError(null);
      } else {
        console.error('Dữ liệu nhóm không phải là mảng:', allTeamsData);
        setError('Không thể lấy danh sách nhóm');
      }
    } catch (err) {
      console.error('Lỗi khi lấy dữ liệu nhóm:', err);
      setError('Không thể kết nối đến API');
    } finally {
      setLoading(false);
    }
  };
  
  // Hàm lấy dữ liệu nhóm của người dùng
  const fetchTeamsData = async () => {
    try {
      // Kiểm tra nếu đã đăng nhập
      if (!isAuthenticated()) {
        setError('Vui lòng đăng nhập để xem thông tin nhóm');
        setLoading(false);
        return;
      }
      
      setLoading(true);
      
      // Lấy userId từ authService
      const userId = getUserId();
      
      if (!userId) {
        throw new Error('Không tìm thấy thông tin người dùng. Vui lòng đăng nhập lại.');
      }
      
      // Sử dụng hàm từ teamsService thay vì gọi API trực tiếp
      const teamsData = await getTeamsByUserId(userId);
      setMyTeams(teamsData);
      setError(null);
    } catch (err) {
      console.error('Lỗi khi lấy dữ liệu nhóm của người dùng:', err);
      setError('Không thể kết nối đến API');
    } finally {
      setLoading(false);
    }
  };
  
  // Lấy danh sách giải đấu khi mở modal tạo nhóm
  const fetchTournaments = async () => {
    try {
      setTournamentsLoading(true);
      // Truyền false vào hàm getAllTournaments để lấy tất cả giải đấu, kể cả bị ẩn
      const data = await getAllTournaments(false);
      setTournaments(data);
    } catch (error) {
      console.error('Error fetching tournaments:', error);
    } finally {
      setTournamentsLoading(false);
    }
  };
  
  // Mở modal tạo nhóm và lấy danh sách giải đấu
  const handleOpenCreateModal = () => {
    setShowCreateModal(true);
    fetchTournaments();
    setCreateTeamData({
      name: '',
      description: '',
      tournamentId: undefined
    });
    setCreateError(null);
  };
  
  // Hàm xử lý tạo nhóm mới
  const handleCreateTeam = async () => {
    try {
      setCreateLoading(true);
      setCreateError(null);
      
      // Lấy userId từ authService
      const userId = getUserId();
      
      if (!userId) {
        throw new Error('Không tìm thấy thông tin người dùng. Vui lòng đăng nhập lại.');
      }
      
      // Chuẩn bị dữ liệu để tạo team
      const teamData: CreateTeamRequest = {
        name: createTeamData.name,
        description: createTeamData.description,
        tournamentId: createTeamData.tournamentId
      };
      
      // Gọi API tạo team
      const newTeam = await createTeam(teamData);
      
      // Cập nhật danh sách nhóm bằng cách gọi API fetch lại dữ liệu
      fetchTeamsData();
      fetchPublicTeams();
      
      // Chuyển tab sang "Nhóm của tôi"
      setActiveTab('myTeams');
      
      // Đóng modal
      setShowCreateModal(false);
      
      // Reset form
      setCreateTeamData({
        name: '',
        description: '',
        tournamentId: undefined
      });
      
      // Hiển thị thông báo thành công
      showMessage('Thành công', 'Tạo nhóm mới thành công!');
    } catch (error: any) {
      console.error('Lỗi khi tạo nhóm:', error);
      setCreateError(error.message || 'Không thể tạo nhóm mới');
      showMessage('Lỗi', error.message || 'Không thể tạo nhóm mới', 'error');
    } finally {
      setCreateLoading(false);
    }
  };
  
  // Xử lý thay đổi input form
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    
    if (type === 'checkbox') {
      const checked = (e.target as HTMLInputElement).checked;
      setCreateTeamData(prev => ({
        ...prev,
        [name]: checked
      }));
    } else {
      setCreateTeamData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };
  
  // Hàm lấy thông tin kết nối Strava của người dùng
  const fetchStravaConnections = async () => {
    try {
      setLoadingStravaConnections(true);
      const stravaData = await getUsersStravaConnection();
      setStravaConnections(stravaData.users);
      console.log('Đã lấy thông tin kết nối Strava:', stravaData);
    } catch (error) {
      console.error('Lỗi khi lấy thông tin kết nối Strava:', error);
    } finally {
      setLoadingStravaConnections(false);
    }
  };
  
  // Hàm kiểm tra trạng thái kết nối Strava của thành viên
  const isStravaConnected = (userId: number) => {
    const user = stravaConnections.find(u => u.userId === userId);
    return user?.stravaConnected || false;
  };
  
  // Hàm xử lý xem chi tiết nhóm
  const handleViewTeamDetail = async (team: Team) => {
    try {
      setSelectedTeam(team);
      
      // Lấy thông tin chi tiết về nhóm
      const teamDetail = await getTeamById(team.id);
      setSelectedTeam(teamDetail);
      
      // Lấy danh sách thành viên trong nhóm
      const membersData = await getTeamMembers(team.id);
      setTeamMembers(membersData);
      
      // Lấy thông tin kết nối Strava
      fetchStravaConnections();
      
      setShowDetailModal(true);
    } catch (error) {
      console.error('Lỗi khi lấy chi tiết nhóm:', error);
      alert('Không thể lấy chi tiết nhóm');
    }
  };
  
  // Hàm lấy danh sách người dùng
  const fetchUsers = async () => {
    try {
      setLoadingUsers(true);
      const usersData = await getAllUsers();
      console.log('Danh sách người dùng:', usersData);
      const userOptions = usersData.map(user => ({
        userId: user.userId,
        fullName: user.fullName || `Người dùng #${user.userId}`,
        avatarUrl: user.avatarUrl
      }));
      setUsers(userOptions);
      setFilteredUsers(userOptions);
    } catch (error) {
      console.error('Lỗi khi lấy danh sách người dùng:', error);
    } finally {
      setLoadingUsers(false);
    }
  };
  
  // Lọc danh sách người dùng khi tìm kiếm
  useEffect(() => {
    if (userSearchQuery.trim() === '') {
      setFilteredUsers(users);
    } else {
      const query = userSearchQuery.toLowerCase();
      const filtered = users.filter(user => 
        user.fullName.toLowerCase().includes(query) || 
        user.userId.toString().includes(query)
      );
      setFilteredUsers(filtered);
    }
  }, [userSearchQuery, users]);
  
  // Hàm để thêm thành viên mới vào nhóm
  const handleAddMember = async () => {
    if (!selectedTeam || !selectedUserId) return;
    
    setMemberActionLoading(true);
    setMemberActionError(null);
    
    try {
      // Kiểm tra xem người dùng hiện tại đã đăng nhập chưa
      if (!isAuthenticated()) {
        throw new Error('Bạn cần đăng nhập để thực hiện chức năng này');
      }
      
      // Kiểm tra xem người dùng được thêm có phải là thành viên của nhóm này chưa
      const isAlreadyMember = teamMembers.some(member => member.id === selectedUserId);
      if (isAlreadyMember) {
        throw new Error('Người dùng này đã là thành viên của nhóm');
      }
      
      // Sử dụng API để thêm thành viên vào nhóm bằng userId
      await addTeamMemberByUserId(selectedTeam.id, selectedUserId);
      
      // Cập nhật danh sách thành viên
      const membersData = await getTeamMembers(selectedTeam.id);
      setTeamMembers(membersData);
      
      // Cập nhật thông tin nhóm
      const updatedTeam = await getTeamById(selectedTeam.id);
        setSelectedTeam(updatedTeam);
        
      // Cập nhật danh sách nhóm
      setMyTeams(prevTeams => 
        prevTeams.map(team => team.id === selectedTeam.id ? updatedTeam : team)
      );
      
      setPublicTeams(prevTeams => 
        prevTeams.map(team => team.id === selectedTeam.id ? updatedTeam : team)
      );
      
      // Reset form
      setSelectedUserId(null);
      setUserSearchQuery('');
      setShowUserDropdown(false);
      
      // Hiển thị thông báo thành công
      showMessage('Thành công', 'Thêm thành viên vào nhóm thành công!');
    } catch (error: any) {
      console.error('Lỗi khi thêm thành viên:', error);
      
      // Hiển thị thông báo lỗi từ API
      const errorMessage = error.message || 'Không thể thêm thành viên vào nhóm';
      setMemberActionError(errorMessage);
      
      // Hiển thị thông báo lỗi dựa trên loại lỗi
      if (errorMessage.includes('đã là thành viên')) {
        showMessage('Lỗi', errorMessage, 'error');
      } else {
        showMessage('Lỗi', errorMessage, 'error');
      }
    } finally {
      setMemberActionLoading(false);
    }
  };
  
  // Hàm để xóa thành viên khỏi nhóm
  const handleRemoveMember = async (teamId: number, clubMemberId: number) => {
    try {
    setMemberActionLoading(true);
    setMemberActionError(null);
    
      // Gọi API xóa thành viên
      const updatedTeam = await removeMemberFromTeam(teamId, clubMemberId);
      
      // Cập nhật danh sách thành viên
      const membersData = await getTeamMembers(teamId);
      setTeamMembers(membersData);
      
      // Cập nhật thông tin nhóm
        setSelectedTeam(updatedTeam);
        
      // Cập nhật danh sách nhóm
      setMyTeams(prevTeams => 
        prevTeams.map(team => team.id === teamId ? updatedTeam : team)
      );
      
      setPublicTeams(prevTeams => 
        prevTeams.map(team => team.id === teamId ? updatedTeam : team)
      );
      
      // Hiển thị thông báo thành công
      showMessage('Thành công', 'Xóa thành viên khỏi nhóm thành công!');
    } catch (error: any) {
      console.error('Lỗi khi xóa thành viên:', error);
      setMemberActionError(error.message || 'Không thể xóa thành viên khỏi nhóm');
      showMessage('Lỗi', error.message || 'Không thể xóa thành viên khỏi nhóm', 'error');
    } finally {
      setMemberActionLoading(false);
    }
  };
  
  // Hàm sửa vai trò của thành viên (nâng cấp thành admin hoặc hạ xuống thành viên thường)
  const handleChangeMemberRole = async (userId: number, newRole: string) => {
    if (!selectedTeam) return;
    
    setMemberActionLoading(true);
    setMemberActionError(null);
    
    try {
      // Ở đây sẽ gọi API để thay đổi vai trò thành viên
      // Giả lập việc thay đổi vai trò trong khi chưa có API thực tế

      // Thay thế setTimeout bằng code xử lý trực tiếp
      // để cập nhật dữ liệu ngay khi API thành công
      await Promise.all([fetchTeamsData(), fetchPublicTeams()]);
      
      // Cập nhật lại nhóm được chọn để hiển thị thông tin mới nhất
      const updatedTeam = myTeams.find(team => team.id === selectedTeam.id);
      if (updatedTeam) {
        setSelectedTeam(updatedTeam);
        
        // Cập nhật lại danh sách thành viên
        const members = await getTeamMembers(updatedTeam.id);
        setTeamMembers(members);
      }
      
    } catch (error: any) {
      setMemberActionError(error.message || 'Không thể thay đổi vai trò thành viên');
    } finally {
      setMemberActionLoading(false);
    }
  };
  
  // Hàm xử lý cập nhật nhóm
  const handleUpdateTeam = async () => {
    try {
      if (!selectedTeam) return;
    
      setUpdateLoading(true);
      setUpdateError(null);
      
      // Chuẩn bị dữ liệu để cập nhật team
      const teamData: UpdateTeamRequest = {
        name: updateTeamData.name,
        description: updateTeamData.description,
        tournamentId: updateTeamData.tournamentId
      };
      
      // Gọi API cập nhật team
      const updatedTeam = await updateTeam(selectedTeam.id, teamData);
      
      // Cập nhật danh sách nhóm bằng cách gọi API fetch lại dữ liệu
      fetchTeamsData();
      fetchPublicTeams();
      
      // Cập nhật thông tin nhóm trong modal chi tiết
      setSelectedTeam(updatedTeam);
      
      // Đóng modal cập nhật
      setShowUpdateModal(false);
      
      // Hiển thị thông báo thành công
      showMessage('Thành công', 'Cập nhật nhóm thành công!');
    } catch (error: any) {
      console.error('Lỗi khi cập nhật nhóm:', error);
      setUpdateError(error.message || 'Không thể cập nhật nhóm');
      showMessage('Lỗi', error.message || 'Không thể cập nhật nhóm', 'error');
    } finally {
      setUpdateLoading(false);
    }
  };
  
  // Hàm mở modal cập nhật nhóm
  const handleShowUpdateModal = (team: Team) => {
    setSelectedTeam(team);
    setUpdateTeamData({
      name: team.name,
      description: team.description || '',
      tournamentId: team.tournamentId
    });
    // Đóng modal chi tiết khi mở modal cập nhật
    setShowDetailModal(false);
    setShowUpdateModal(true);
    setUpdateError(null);
  };
  
  // Xử lý thay đổi input form cập nhật
  const handleUpdateInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setUpdateTeamData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  
  // Hàm để tham gia nhóm
  const handleJoinTeam = async (team: Team) => {
    setSelectedTeam(team);
    setJoinTeamLoading(true);
    setMemberActionError(null);
    
    try {
      // Lấy userId từ authService
      const userId = getUserId();
      
      if (!userId) {
        throw new Error('Không tìm thấy thông tin người dùng. Vui lòng đăng nhập lại.');
      }
      
      // Thêm thành viên vào nhóm
      await addMemberToTeam(team.id, userId);
      
      // Lấy thông tin nhóm đã cập nhật
      const updatedTeam = await getTeamById(team.id);
      setSelectedTeam(updatedTeam);
      
      // Lấy danh sách thành viên mới sau khi thêm thành viên
      const membersData = await getTeamMembers(team.id);
      setTeamMembers(membersData);
      
      // Cập nhật lại dữ liệu sau khi tham gia thành công
      fetchTeamsData();
      fetchPublicTeams();
      
      // Hiển thị thông báo thành công
      showMessage('Thành công', 'Bạn đã tham gia nhóm thành công!');
    } catch (error: any) {
      setMemberActionError(error.message || 'Không thể tham gia nhóm. Vui lòng thử lại sau.');
      showMessage('Lỗi', error.message || 'Không thể tham gia nhóm. Vui lòng thử lại sau.', 'error');
    } finally {
      setJoinTeamLoading(false);
    }
  };
  
  // Hàm để rời nhóm
  const handleLeaveTeam = async () => {
    if (!selectedTeam) return;
    
    setLeaveTeamLoading(true);
    setMemberActionError(null);
    
    try {
      // Lấy userId từ authService
      const userId = getUserId();
      
      if (!userId) {
        throw new Error('Không tìm thấy thông tin người dùng. Vui lòng đăng nhập lại.');
      }
      
      // Xóa thành viên khỏi nhóm
      await removeMemberFromTeam(selectedTeam.id, userId);
      
      // Cập nhật lại dữ liệu sau khi rời nhóm thành công
      fetchTeamsData();
      fetchPublicTeams();
      
      // Đóng modal
      setShowDetailModal(false);
      
      // Hiển thị thông báo thành công
      showMessage('Thành công', 'Bạn đã rời khỏi nhóm thành công!');
    } catch (error: any) {
      setMemberActionError(error.message || 'Không thể rời khỏi nhóm. Vui lòng thử lại sau.');
      showMessage('Lỗi', error.message || 'Không thể rời khỏi nhóm. Vui lòng thử lại sau.', 'error');
    } finally {
      setLeaveTeamLoading(false);
    }
  };
  
  // Thêm useEffect để lấy thông tin kết nối Strava khi mở modal chi tiết nhóm
  useEffect(() => {
    if (showDetailModal) {
      fetchStravaConnections();
    }
  }, [showDetailModal]);
  
  // Thêm useEffect để tải danh sách người dùng khi hiển thị modal chi tiết nhóm
  useEffect(() => {
    if (showDetailModal && currentUser && (selectedTeam?.captainId === currentUser.id || isUserAdmin || isAdmin())) {
      fetchUsers();
    }
  }, [showDetailModal, currentUser, selectedTeam]);
  
  // Lấy dữ liệu nhóm khi component được mount
  useEffect(() => {
    fetchTeamsData();
    fetchPublicTeams();
  }, []);

  // Thêm chức năng gọi lại API khi quay lại trang
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        // Khi người dùng quay lại tab, gọi lại API
        fetchTeamsData();
        fetchPublicTeams();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  // Thêm useEffect để gọi API khi thay đổi tab
  useEffect(() => {
    if (activeTab === 'myTeams') {
      fetchTeamsData();
    } else {
      fetchPublicTeams();
    }
  }, [activeTab]);

  // Tìm kiếm nhóm trong danh sách nhóm hiện tại
  const filteredTeams = React.useMemo(() => {
    // Đảm bảo myTeams và publicTeams luôn là mảng
    const currentTeams = activeTab === 'myTeams' 
      ? (Array.isArray(myTeams) ? myTeams : []) 
      : (Array.isArray(publicTeams) ? publicTeams : []);
    
    if (searchQuery.trim() === '') {
      return currentTeams;
    }
    
    return currentTeams.filter(team => 
      team?.name?.toLowerCase().includes(searchQuery.toLowerCase())
    );
  }, [activeTab, myTeams, publicTeams, searchQuery]);
      
  // Lọc danh sách thành viên khi tìm kiếm
  const filteredMembers = searchMemberQuery.trim() !== ''
    ? teamMembers.filter(member => 
        (member?.user?.username || '').toLowerCase().includes(searchMemberQuery.toLowerCase())
      )
    : teamMembers;

  // Cập nhật useEffect để lưu danh sách nhóm và giải đấu mà người dùng đã tham gia
  useEffect(() => {
    if (myTeams.length > 0) {
      // Lưu thông tin nhóm và giải đấu
      const teamsInfo = myTeams.map(team => ({
        teamId: team.id,
        tournamentId: team.tournamentId
      }));
      setUserTeams(teamsInfo);
      
      // Vẫn giữ lại userTeamId cho khả năng tương thích
      setUserTeamId(myTeams[0].id);
    } else {
      setUserTeams([]);
      setUserTeamId(null);
    }
  }, [myTeams]);

  // Hàm hiển thị thông báo
  const showMessage = (title: string, message: string, type: 'success' | 'error' = 'success') => {
    setMessageDialogContent({ title, message, type });
    setShowMessageDialog(true);
    
    // Tự động ẩn thông báo sau 3 giây
    setTimeout(() => {
      setShowMessageDialog(false);
    }, 3000);
  };

  // Hàm tiện ích để xử lý định dạng ngày
  const formatDateValue = (dateValue: string | number[] | undefined): string => {
    if (!dateValue) return 'N/A';
    
    if (Array.isArray(dateValue)) {
      // Mảng có dạng [năm, tháng, ngày, ...] 
      const [year, month, day] = dateValue;
      // Tạo chuỗi ISO trực tiếp để tránh bị lệch ngày
      const isoString = `${year}-${month.toString().padStart(2, '0')}-${day.toString().padStart(2, '0')}T00:00:00`;
      return new Date(isoString).toLocaleDateString('vi-VN');
    }
    
    return new Date(dateValue).toLocaleDateString('vi-VN');
  };

  const { theme } = useTheme();
  
  // Hiển thị loading state
  if (loading) {
    return (
      <div className="loading-state">
        <FaSpinner className="loading-spinner" size={40} color="#20757A" />
      </div>
    );
  }

  // Hiển thị lỗi nếu có
  if (error) {
    return (
      <div className="teams-container">
        <div className="error-message" style={{ 
          padding: '20px', 
          backgroundColor: 'rgba(244, 67, 54, 0.1)', 
          color: COLORS.status.error, 
          borderRadius: '8px',
          marginBottom: '20px',
          display: 'flex',
          alignItems: 'center',
          gap: '12px'
        }}>
          <FaExclamationTriangle size={24} />
          <div>
            <h3 style={{ margin: '0 0 8px 0' }}>Đã xảy ra lỗi</h3>
            <p style={{ margin: 0 }}>{error}</p>
          </div>
        </div>
        
        {!isAuthenticated() && (
          <div style={{ textAlign: 'center', marginTop: '30px' }}>
            <a href="/login" className="btn btn-primary" style={{
              display: 'inline-block',
              padding: '12px 24px',
              textDecoration: 'none',
              borderRadius: '8px',
              fontWeight: 'bold'
            }}>
              Đăng nhập
            </a>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className={`teams-container ${theme}`}>
      <div className="teams-header">
        <h1 className="teams-title">
          <FaUsers style={{ marginRight: '12px' }} /> Nhóm
        </h1>
        
        {/* Nút tạo nhóm mới (chỉ hiển thị cho admin) */}
        {(isUserAdmin || isAdmin()) && (
          <button
            onClick={handleOpenCreateModal}
            className="btn btn-primary"
            style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
          >
            <FaPlus size={14} /> Tạo nhóm mới
          </button>
        )}
      </div>
      
      {/* Tab điều hướng */}
      <div className="teams-tabs">
        <button 
          onClick={() => setActiveTab('myTeams')}
          className={`teams-tab ${activeTab === 'myTeams' ? 'active' : ''}`}
        >
          Nhóm của tôi
        </button>
        <button 
          onClick={() => {
            setActiveTab('publicTeams');
            fetchPublicTeams();
          }}
          className={`teams-tab ${activeTab === 'publicTeams' ? 'active' : ''}`}
        >
          Tất cả các nhóm
        </button>
      </div>

      {/* Thanh tìm kiếm */}
      <div className="teams-search-container">
        <FaSearch className="teams-search-icon" />
        <input
          type="text"
          placeholder="Tìm kiếm nhóm..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="teams-search-input"
        />
      </div>
      
      {/* Tab "Nhóm của tôi" */}
      {activeTab === 'myTeams' && (
        <div>
          {filteredTeams.length === 0 ? (
            <div className="empty-state">
              <div className="empty-state-icon">
                <FaUsers />
              </div>
              <h3 className="empty-state-title">Bạn chưa tham gia nhóm nào</h3>
              <p className="empty-state-text">
                Hãy tham gia một nhóm để theo dõi hoạt động và thành tích cùng các thành viên khác.
              </p>
              <div className="empty-state-action">
                <button 
                  onClick={() => setActiveTab('publicTeams')}
                  className="btn btn-primary"
                >
                  Khám phá các nhóm
                </button>
              </div>
            </div>
          ) : (
            <div className="teams-grid">
              {filteredTeams.map((team) => (
                <div key={team.id} className="team-card">
                  <div className="team-card-header">
                    <h3 className="team-card-title">{team.name}</h3>
                    {team.tournamentName && (
                      <div className="team-card-badge">{team.tournamentName}</div>
                    )}
                  </div>
                  
                  <div className="team-card-content">
                    <p className="team-card-description">
                      {team.description || 'Không có mô tả.'}
                    </p>
                    
                    <div className="team-card-stats">
                      <div className="team-card-stat">
                        <div className="team-card-stat-value">{team.memberCount || 0}</div>
                        <div className="team-card-stat-label">Thành viên</div>
                      </div>
                      
                      <div className="team-card-stat">
                        <div className="team-card-stat-value">{formatDateValue(team.createdAt)}</div>
                        <div className="team-card-stat-label">Ngày tạo</div>
                      </div>
                    </div>
                  </div>
                  
                                      <div className="team-card-footer">
                    <div className="team-card-creator">
                      <span>{team.captainName || 'Không có quản trị viên'}</span>
                    </div>
                    
                    <div className="team-card-actions">
                      <button
                        title="Xem chi tiết"
                        onClick={() => handleViewTeamDetail(team)}
                        className="team-card-action-btn view"
                      >
                        <FaEye size={14} />
                      </button>
                      
                      {/* Hiển thị nút sửa và xóa nếu người dùng là admin của nhóm hoặc admin hệ thống */}
                      {(currentUser && (team.captainId === currentUser.id || isUserAdmin || isAdmin())) && (
                        <>
                          <button
                            title="Sửa thông tin"
                            onClick={() => handleShowUpdateModal(team)}
                            className="team-card-action-btn edit"
                          >
                            <FaEdit size={14} />
                          </button>
                          <button
                            title="Xóa nhóm"
                            onClick={() => {
                              if(window.confirm('Bạn có chắc chắn muốn xóa nhóm này?')) {
                                deleteTeam(team.id).then(() => {
                                  fetchTeamsData();
                                  fetchPublicTeams();
                                  showMessage('Thành công', 'Xóa nhóm thành công!');
                                }).catch(err => {
                                  showMessage('Lỗi', err.message || 'Không thể xóa nhóm', 'error');
                                });
                              }
                            }}
                            className="team-card-action-btn delete"
                          >
                            <FaTrash size={14} />
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
            
      {/* Tab "Tất cả các nhóm" */}
      {activeTab === 'publicTeams' && (
        <div>
          {filteredTeams.length === 0 ? (
            <div className="empty-state">
              <div className="empty-state-icon">
                <FaUsers />
              </div>
              <h3 className="empty-state-title">Không tìm thấy nhóm nào</h3>
              <p className="empty-state-text">
                Hiện tại chưa có nhóm nào được tạo hoặc không có nhóm phù hợp với tìm kiếm của bạn.
              </p>
              {(isUserAdmin || isAdmin()) && (
                <div className="empty-state-action">
                  <button 
                    onClick={handleOpenCreateModal}
                    className="btn btn-primary"
                  >
                    <FaPlus size={14} style={{ marginRight: '8px' }} /> Tạo nhóm mới
                  </button>
                </div>
              )}
            </div>
          ) : (
            <div className="teams-grid">
              {filteredTeams.map((team) => (
                <div key={team.id} className="team-card">
                  <div className="team-card-header">
                    <h3 className="team-card-title">{team.name}</h3>
                    {team.tournamentName && (
                      <div className="team-card-badge">{team.tournamentName}</div>
                    )}
                  </div>
                  
                  <div className="team-card-content">
                    <p className="team-card-description">
                      {team.description || 'Không có mô tả.'}
                    </p>
                    
                    <div className="team-card-stats">
                      <div className="team-card-stat">
                        <div className="team-card-stat-value">{team.memberCount || 0}</div>
                        <div className="team-card-stat-label">Thành viên</div>
                      </div>
                      
                      <div className="team-card-stat">
                        <div className="team-card-stat-value">{formatDateValue(team.createdAt)}</div>
                        <div className="team-card-stat-label">Ngày tạo</div>
                      </div>
                    </div>
                  </div>
                  
                  <div className="team-card-footer">
                    <div className="team-card-creator">
                      <div className="team-card-creator-avatar">
                        {team.captainName ? team.captainName.charAt(0).toUpperCase() : 'A'}
                      </div>
                      <span>{team.captainName || 'Không có quản trị viên'}</span>
                    </div>
                    
                    <div className="team-card-actions">
                      <button
                        title="Xem chi tiết"
                        onClick={() => handleViewTeamDetail(team)}
                        className="team-card-action-btn view"
                      >
                        <FaEye size={14} />
                      </button>
                      
                      {/* Hiển thị nút sửa và xóa nếu người dùng là admin của nhóm hoặc admin hệ thống */}
                      {(currentUser && (team.captainId === currentUser.id || isUserAdmin || isAdmin())) && (
                        <>
                          <button
                            title="Sửa thông tin"
                            onClick={() => handleShowUpdateModal(team)}
                            className="team-card-action-btn edit"
                          >
                            <FaEdit size={14} />
                          </button>
                          <button
                            title="Xóa nhóm"
                            onClick={() => {
                              if(window.confirm('Bạn có chắc chắn muốn xóa nhóm này?')) {
                                deleteTeam(team.id).then(() => {
                                  fetchTeamsData();
                                  fetchPublicTeams();
                                  showMessage('Thành công', 'Xóa nhóm thành công!');
                                }).catch(err => {
                                  showMessage('Lỗi', err.message || 'Không thể xóa nhóm', 'error');
                                });
                              }
                            }}
                            className="team-card-action-btn delete"
                          >
                            <FaTrash size={14} />
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
      
      {/* Modal tạo nhóm mới */}
      {showCreateModal && (
        <div 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 100
          }}
          onClick={() => setShowCreateModal(false)}
        >
          <div 
            style={{
              backgroundColor: COLORS.background.main,
              padding: '20px',
              borderRadius: '8px',
              width: '500px',
              maxWidth: '90%',
              border: `1px solid ${COLORS.border}`
            }}
            onClick={(e) => e.stopPropagation()} // Ngăn sự kiện lan ra phần nền
          >
            <h2 style={{ color: COLORS.text.light, marginTop: 0 }}>Tạo nhóm mới</h2>
            
            {createError && (
              <div style={{ 
                padding: '10px', 
                backgroundColor: 'rgba(255,0,0,0.1)', 
                color: COLORS.status.error, 
                borderRadius: '4px',
                marginBottom: '20px'
              }}>
                {createError}
              </div>
            )}
            
            <form onSubmit={(e) => { e.preventDefault(); handleCreateTeam(); }}>
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', marginBottom: '8px', color: COLORS.text.medium }}>
                  Tên nhóm *
                </label>
                <input
                  type="text"
                  name="name"
                  value={createTeamData.name}
                  onChange={handleInputChange}
                  required
                  style={{
                    width: '100%',
                    padding: '8px',
                    backgroundColor: COLORS.background.light,
                    border: `1px solid ${COLORS.border}`,
                    borderRadius: '4px',
                    color: COLORS.text.light
                  }}
                />
              </div>
              
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', marginBottom: '8px', color: COLORS.text.medium }}>
                  Mô tả
                </label>
                <textarea
                  name="description"
                  value={createTeamData.description}
                  onChange={handleInputChange}
                  style={{
                    width: '100%',
                    padding: '8px',
                    backgroundColor: COLORS.background.light,
                    border: `1px solid ${COLORS.border}`,
                    borderRadius: '4px',
                    color: COLORS.text.light,
                    minHeight: '100px'
                  }}
                />
              </div>
              
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', marginBottom: '8px', color: COLORS.text.medium }}>
                  Giải đấu (tùy chọn)
                </label>
                <select
                  name="tournamentId"
                  value={createTeamData.tournamentId || ''}
                  onChange={handleInputChange}
                  style={{
                    width: '100%',
                    padding: '8px',
                    backgroundColor: COLORS.background.light,
                    border: `1px solid ${COLORS.border}`,
                    borderRadius: '4px',
                    color: COLORS.text.light
                  }}
                >
                  <option value="">-- Không liên kết với giải đấu --</option>
                  {tournaments.map(tournament => (
                    <option key={tournament.id} value={tournament.id}>
                      {tournament.name}
                    </option>
                  ))}
                </select>
                {tournamentsLoading && <div style={{ color: COLORS.text.dark, marginTop: '5px' }}>Đang tải giải đấu...</div>}
              </div>
              
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
                <button
                  type="button"
                  onClick={() => setShowCreateModal(false)}
                  style={{
                    padding: '8px 16px',
                    backgroundColor: 'transparent',
                    color: COLORS.text.medium,
                    border: `1px solid ${COLORS.border}`,
                    borderRadius: '4px',
                    cursor: 'pointer'
                  }}
                >
                  Hủy
                </button>
                <button
                  type="submit"
                  disabled={createLoading}
                  style={{
                    padding: '8px 16px',
                    backgroundColor: COLORS.primary,
                    color: COLORS.text.light,
                    border: 'none',
                    borderRadius: '4px',
                    cursor: createLoading ? 'not-allowed' : 'pointer',
                    opacity: createLoading ? 0.7 : 1
                  }}
                >
                  {createLoading ? 'Đang tạo...' : 'Tạo nhóm'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal xem chi tiết nhóm */}
      {showDetailModal && selectedTeam && (
        <div 
          className="team-detail-modal"
          onClick={() => setShowDetailModal(false)}
        >
          <div 
            className="team-detail-content"
            onClick={(e) => e.stopPropagation()} // Ngăn sự kiện lan ra phần nền
          >
            <div className="team-detail-header">
              <div className="team-detail-title-section">
                <h2 className="team-detail-title">{selectedTeam.name}</h2>
                <div className="team-detail-subtitle">
                  <FaCalendarAlt size={14} /> Ngày tạo: {formatDateValue(selectedTeam.createdAt)}
                  {selectedTeam.tournamentName && (
                    <>
                      <span style={{ margin: '0 8px' }}>•</span>
                      <span>Giải đấu: {selectedTeam.tournamentName}</span>
                    </>
                  )}
                </div>
              </div>
              <button
                onClick={() => setShowDetailModal(false)}
                className="team-detail-close"
                aria-label="Đóng"
              >
                <FaTimes />
              </button>
            </div>
            
            <div className="team-detail-body">
              {memberActionError && (
                <div style={{ 
                  padding: '12px', 
                  backgroundColor: 'rgba(244, 67, 54, 0.1)', 
                  color: COLORS.status.error, 
                  borderRadius: '8px',
                  marginBottom: '16px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}>
                  <FaExclamationTriangle size={16} />
                  {memberActionError}
                </div>
              )}
              
              {/* Thông tin nhóm */}
              <div className="team-detail-section">
                <h3 className="team-detail-section-title">
                  <FaInfoCircle /> Thông tin nhóm
                </h3>
                <p className="team-detail-description">
                  {selectedTeam.description || 'Không có mô tả cho nhóm này.'}
                </p>
                
                <div className="team-detail-stats">
                  <div className="team-detail-stat">
                    <div className="team-detail-stat-value">
                      {teamMembers.length}
                    </div>
                    <div className="team-detail-stat-label">Thành viên</div>
                  </div>
                  
                  <div className="team-detail-stat">
                    <div className="team-detail-stat-value">
                      {selectedTeam.captainName || 'N/A'}
                    </div>
                    <div className="team-detail-stat-label">Người tạo</div>
                  </div>
                  
                  <div className="team-detail-stat">
                    <div className="team-detail-stat-value">
                      {formatDateValue(selectedTeam.createdAt)}
                    </div>
                    <div className="team-detail-stat-label">Ngày tạo</div>
                  </div>
                </div>
                
                {/* Hiển thị nút tham gia/rời nhóm cho người dùng đã đăng nhập */}
                {currentUser && (
                  <div style={{ marginTop: '20px' }}>
                    {/* Nếu người dùng đã là thành viên của nhóm này, hiển thị nút "Rời nhóm" */}
                    {userTeams.some(team => team.teamId === selectedTeam.id) ? (
                      <button
                        onClick={handleLeaveTeam}
                        disabled={leaveTeamLoading}
                        className="btn btn-danger"
                      >
                        {leaveTeamLoading ? (
                          <>
                            <FaSpinner className="loading-spinner" style={{ marginRight: '8px' }} /> Đang xử lý...
                          </>
                        ) : 'Rời nhóm'}
                      </button>
                    ) : userTeams.some(team => team.tournamentId === selectedTeam.tournamentId && selectedTeam.tournamentId !== undefined) ? (
                      <div style={{ 
                        padding: '12px', 
                        backgroundColor: 'rgba(255, 193, 7, 0.1)', 
                        color: '#FFC107', 
                        borderRadius: '8px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px'
                      }}>
                        <FaExclamationTriangle size={16} />
                        Bạn đã là thành viên của một nhóm khác trong cùng giải đấu này. Vui lòng rời nhóm đó trước khi tham gia nhóm mới trong cùng giải đấu.
                      </div>
                    ) : (
                      <button
                        onClick={() => handleJoinTeam(selectedTeam)}
                        disabled={joinTeamLoading}
                        className="btn btn-primary"
                      >
                        {joinTeamLoading ? (
                          <>
                            <FaSpinner className="loading-spinner" style={{ marginRight: '8px' }} /> Đang xử lý...
                          </>
                        ) : 'Tham gia nhóm'}
                      </button>
                    )}

                    {/* Nút sửa thông tin nhóm - chỉ cho admin của nhóm hoặc admin hệ thống */}
                    {(selectedTeam?.captainId === currentUser.id || isUserAdmin || isAdmin()) && (
                    <button
                      onClick={() => handleShowUpdateModal(selectedTeam)}
                      className="btn btn-cancel"
                      style={{ marginLeft: '10px' }}
                    >
                      <FaEdit style={{ marginRight: '8px' }} /> Sửa thông tin
                    </button>
                  )}
                  </div>
              )}
            </div>
              
            {/* Danh sách thành viên */}
            <div className="team-detail-section">
              <h3 className="team-detail-section-title">
                <FaUserFriends /> Danh sách thành viên
              </h3>
              
              {/* Tìm kiếm thành viên */}
              <div style={{ marginBottom: '16px' }}>
                <div className="teams-search-container" style={{ marginBottom: '0' }}>
                  <FaSearch className="teams-search-icon" />
                  <input
                    type="text"
                    placeholder="Tìm kiếm thành viên..."
                    value={searchMemberQuery}
                    onChange={(e) => setSearchMemberQuery(e.target.value)}
                    className="teams-search-input"
                  />
              </div>

              </div>
                
                {/* Bảng danh sách thành viên */}
                {filteredMembers.length === 0 ? (
                  <div className="empty-state" style={{ padding: '24px 0' }}>
                    <div className="empty-state-icon">
                      <FaUserFriends />
                    </div>
                    <p className="empty-state-text">Không tìm thấy thành viên nào.</p>
                  </div>
                ) : (
                  <table className="members-table">
                    <thead>
                      <tr>
                        <th style={{ width: '50px' }}>#</th>
                        <th>Thành viên</th>
                        <th>Email</th>
                        <th>Vai trò</th>
                        <th>Kết nối Strava</th>
                        <th style={{ width: '100px', textAlign: 'center' }}>Hành động</th>
                      </tr>
                    </thead>
                    <tbody>
                      {memberActionLoading ? (
                        <tr>
                          <td colSpan={6} style={{ textAlign: 'center', padding: '20px' }}>
                            <FaSpinner className="loading-spinner" size={24} />
                          </td>
                        </tr>
                      ) : (
                        filteredMembers.map((member, index) => (
                          <tr key={member.id}>
                            <td>{index + 1}</td>
                            <td>
                              <div className="member-info">
                                <span className="member-name">
                                  {member.firstName || member.firstname || ''} {member.lastName || member.lastname || ''}
                                  {!member.firstName && !member.firstname && (member.username || member.user?.username || 'Không có tên')}
                                </span>
                              </div>
                            </td>
                            <td>{member.email || 'N/A'}</td>
                            <td>
                              <span className={`member-role ${member.isAdmin || member.role === 'ADMIN' || member.role === 'ROLE_ADMIN' ? 'admin' : ''}`}>
                                {member.isAdmin || member.role === 'ADMIN' || member.role === 'ROLE_ADMIN' ? 'Admin' : 'Thành viên'}
                              </span>
                            </td>
                            <td>
                              <button
                                className={`strava-connect-btn ${member.stravaConnected || isStravaConnected(member.id) ? 'connected' : 'not-connected'}`}
                                style={{
                                  padding: '6px 10px',
                                  borderRadius: '4px',
                                  border: 'none',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '6px',
                                  fontSize: '12px',
                                  cursor: 'pointer',
                                  backgroundColor: member.stravaConnected || isStravaConnected(member.id) ? 'rgba(76,175,80,0.2)' : 'rgba(244,67,54,0.1)',
                                  color: member.stravaConnected || isStravaConnected(member.id) ? '#4CAF50' : '#F44336',
                                  fontWeight: 'bold'
                                }}
                              >
                                <FaStrava size={14} />
                                {member.stravaConnected || isStravaConnected(member.id) ? 'Đã kết nối' : 'Chưa kết nối'}
                              </button>
                            </td>
                            <td style={{ textAlign: 'center' }}>
                              {/* Chỉ hiển thị nút xóa thành viên cho admin */}
                              {currentUser && (selectedTeam?.captainId === currentUser.id || isUserAdmin || isAdmin()) && 
                                !(member.role === 'ROLE_ADMIN' || member.isAdmin) && (
                                <button
                                  title="Xóa thành viên"
                                  onClick={() => handleRemoveMember(selectedTeam.id, member.id)}
                                  disabled={memberActionLoading}
                                  className="member-action-btn remove"
                                >
                                  <FaTrash size={12} />
                                </button>
                              )}
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                )}

              {/* Thêm thành viên mới - chỉ hiển thị cho admin của nhóm hoặc admin hệ thống */}
              {currentUser && (selectedTeam.captainId === currentUser.id || isUserAdmin || isAdmin()) && (
                <div style={{ marginTop: '20px' }}>
                  <h4 style={{ color: COLORS.text.light, marginBottom: '12px' }}>Thêm thành viên mới</h4>
                  <div className="add-member-form">
                      <div className="user-dropdown-container" style={{ position: 'relative', width: '100%' }}>
                    <input
                          type="text"
                          placeholder="Tìm kiếm người dùng theo tên..."
                          value={userSearchQuery}
                          onChange={(e) => {
                            setUserSearchQuery(e.target.value);
                            setShowUserDropdown(true);
                          }}
                          onFocus={() => setShowUserDropdown(true)}
                      className="add-member-input"
                          style={{ width: '100%' }}
                        />
                        
                        {showUserDropdown && (
                          <div 
                            className="user-dropdown" 
                            style={{
                              position: 'absolute',
                              top: '100%',
                              left: 0,
                              right: 0,
                              maxHeight: '200px',
                              overflowY: 'auto',
                              backgroundColor: COLORS.background.light,
                              border: `1px solid ${COLORS.border}`,
                              borderRadius: '4px',
                              zIndex: 10,
                              boxShadow: '0 4px 8px rgba(0,0,0,0.1)'
                            }}
                          >
                            {loadingUsers ? (
                              <div style={{ padding: '10px', textAlign: 'center' }}>
                                <FaSpinner className="loading-spinner" size={16} /> Đang tải...
                              </div>
                            ) : filteredUsers.length === 0 ? (
                              <div style={{ padding: '10px', textAlign: 'center', color: COLORS.text.medium }}>
                                Không tìm thấy người dùng nào
                              </div>
                            ) : (
                              filteredUsers.map(user => (
                                <div
                                  key={user.userId}
                                  className="user-option"
                                  onClick={() => {
                                    setSelectedUserId(user.userId);
                                    setUserSearchQuery(user.fullName);
                                    setShowUserDropdown(false);
                                  }}
                                  style={{
                                    padding: '8px 12px',
                                    cursor: 'pointer',
                                    borderBottom: `1px solid ${COLORS.border}`,
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '10px',
                                    color: COLORS.text.light,
                                    backgroundColor: selectedUserId === user.userId ? 'rgba(32, 117, 122, 0.1)' : 'transparent',
                                    transition: 'background-color 0.2s'
                                  }}
                                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'rgba(32, 117, 122, 0.05)'}
                                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = selectedUserId === user.userId ? 'rgba(32, 117, 122, 0.1)' : 'transparent'}
                                >

                                  <div>
                                    <div style={{ fontWeight: 'medium' }}>{user.fullName}</div>
                                    <div style={{ fontSize: '12px', color: COLORS.text.medium }}>ID: {user.userId}</div>
                                  </div>
                                </div>
                              ))
                            )}
                          </div>
                        )}
                        
                        {/* Phía ngoài dropdown để bắt sự kiện click và đóng dropdown */}
                        {showUserDropdown && (
                          <div 
                            style={{
                              position: 'fixed',
                              top: 0,
                              left: 0,
                              right: 0,
                              bottom: 0,
                              zIndex: 9
                            }}
                            onClick={() => setShowUserDropdown(false)}
                          />
                        )}
                      </div>
                    <button
                      onClick={handleAddMember}
                        disabled={memberActionLoading || !selectedUserId}
                      className="add-member-btn"
                    >
                      {memberActionLoading ? (
                        <>
                          <FaSpinner className="loading-spinner" style={{ marginRight: '8px' }} /> Đang thêm...
                        </>
                      ) : (
                        <>
                          <FaUserPlus style={{ marginRight: '8px' }} /> Thêm thành viên
                        </>
                      )}
                    </button>
                  </div>
                </div>
              )}
            </div>
            
            <div className="form-footer">
              <button
                onClick={() => setShowDetailModal(false)}
                className="btn btn-cancel"
              >
                Đóng
              </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal cập nhật nhóm */}
      {showUpdateModal && selectedTeam && (
        <div 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 999
          }}
          onClick={() => setShowUpdateModal(false)}
        >
          <div 
            style={{
              backgroundColor: COLORS.background.main,
              padding: '20px',
              borderRadius: '8px',
              width: '500px',
              maxWidth: '90%',
              border: `1px solid ${COLORS.border}`
            }}
            onClick={(e) => e.stopPropagation()} // Ngăn sự kiện lan ra phần nền
          >
            <h2 style={{ color: COLORS.text.light, marginTop: 0 }}>Cập nhật thông tin nhóm</h2>
            
            {updateError && (
              <div style={{ 
                padding: '10px', 
                backgroundColor: 'rgba(255,0,0,0.1)', 
                color: COLORS.status.error, 
                borderRadius: '4px',
                marginBottom: '20px'
              }}>
                {updateError}
              </div>
            )}
            
            <form onSubmit={(e) => { e.preventDefault(); handleUpdateTeam(); }}>
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', marginBottom: '8px', color: COLORS.text.medium }}>
                  Tên nhóm *
                </label>
                <input
                  type="text"
                  name="name"
                  value={updateTeamData.name}
                  onChange={handleUpdateInputChange}
                  required
                  style={{
                    width: '100%',
                    padding: '8px',
                    backgroundColor: COLORS.background.light,
                    border: `1px solid ${COLORS.border}`,
                    borderRadius: '4px',
                    color: COLORS.text.light
                  }}
                />
              </div>
              
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', marginBottom: '8px', color: COLORS.text.medium }}>
                  Mô tả
                </label>
                <textarea
                  name="description"
                  value={updateTeamData.description}
                  onChange={handleUpdateInputChange}
                  style={{
                    width: '100%',
                    padding: '8px',
                    backgroundColor: COLORS.background.light,
                    border: `1px solid ${COLORS.border}`,
                    borderRadius: '4px',
                    color: COLORS.text.light,
                    minHeight: '100px'
                  }}
                />
              </div>
              
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
                <button
                  type="button"
                  onClick={() => setShowUpdateModal(false)}
                  style={{
                    padding: '8px 16px',
                    backgroundColor: 'transparent',
                    color: COLORS.text.medium,
                    border: `1px solid ${COLORS.border}`,
                    borderRadius: '4px',
                    cursor: 'pointer'
                  }}
                >
                  Hủy
                </button>
                <button
                  type="submit"
                  disabled={updateLoading}
                  style={{
                    padding: '8px 16px',
                    backgroundColor: COLORS.primary,
                    color: COLORS.text.light,
                    border: 'none',
                    borderRadius: '4px',
                    cursor: updateLoading ? 'not-allowed' : 'pointer',
                    opacity: updateLoading ? 0.7 : 1
                  }}
                >
                  {updateLoading ? 'Đang cập nhật...' : 'Cập nhật'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Dialog Message */}
      {showMessageDialog && (
        <div 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.01)',
            zIndex: 999
          }}
          onClick={() => setShowMessageDialog(false)}
        >
          <div 
            style={{
              position: 'fixed',
              bottom: '30px',
              right: '30px',
              backgroundColor: messageDialogContent.type === 'success' ? '#2f855a' : '#e53e3e',
              color: 'white',
              padding: '16px 20px',
              borderRadius: '8px',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
              zIndex: 1000,
              minWidth: '300px',
              maxWidth: '400px',
              animation: 'slideIn 0.3s ease-out',
              transition: 'all 0.3s ease',
              display: 'flex',
              alignItems: 'center'
            }}
            onClick={(e) => e.stopPropagation()} // Ngăn sự kiện lan ra phần nền
          >
            <div style={{ marginRight: '12px', fontSize: '20px' }}>
              {messageDialogContent.type === 'success' ? '✅' : '❌'}
            </div>
            <div>
              <div style={{ fontWeight: 'bold', fontSize: '16px', marginBottom: '4px' }}>
                {messageDialogContent.title}
              </div>
              <div style={{ fontSize: '14px' }}>
                {messageDialogContent.message}
              </div>
            </div>
            <button 
              onClick={() => setShowMessageDialog(false)}
              style={{
                background: 'transparent',
                border: 'none',
                color: 'white',
                marginLeft: 'auto',
                cursor: 'pointer',
                fontSize: '18px',
                opacity: 0.7,
                transition: 'opacity 0.2s'
              }}
              onMouseOver={(e) => e.currentTarget.style.opacity = '1'}
              onMouseOut={(e) => e.currentTarget.style.opacity = '0.7'}
            >
              ×
            </button>
          </div>
        </div>
      )}

      {/* Thêm ChatBox component vào cuối */}
      <ChatBox />
      
    </div>
  );
};

export default Teams; 